/**
 * Maps legacy Beliefnet list IDs and GetResponse campaign IDs to Beehiiv newsletter keys.
 * Sitecore inline signup widgets pass the GetResponse Campaign ID field value via
 * NewsletterCampaignIdResolver. This resolver normalizes all identifier forms to the
 * canonical Beehiiv newsletter key.
 *
 * Matching rules (in order):
 *   1. Numeric input → match listId (listId 0 = no legacy mapping, falls back)
 *   2. Non-numeric → match campaignId, then newsletterKey
 *   3. No match → fall back to best-of-beliefnet
 */
const NEWSLETTER_KEY_MAP: Array<{ listId: number; campaignId: string; key: string }> = [
    { listId: 871, campaignId: 'vs', key: 'omwg' },               // OMWG Bible Reading
    { listId:   0, campaignId: 'Gf', key: 'omwg-daily-trivia' },  // OMWG Daily Trivia
    { listId:   0, campaignId: 'GW', key: 'bible-trivia' },        // Bible Trivia Challenge
    { listId:   0, campaignId: 'v8', key: 'weekly-trivia' },       // Weekly Trivia
    { listId:   0, campaignId: 'Gb', key: 'bnet-daily' },          // Beliefnet Daily Newsletter
    { listId:   8, campaignId: 'vb', key: 'best-of-beliefnet' },   // Best of Beliefnet
    { listId:   7, campaignId: 'vD', key: 'bible-reading' },       // Bible Reading
    { listId:  19, campaignId: 'vk', key: 'health-happiness' },    // Health and Happiness
    { listId: 616, campaignId: 'vF', key: 'love-family' },         // Love and Family
    { listId:  18, campaignId: 'v6', key: 'prayer-of-the-day' },   // Prayer of the Day
    { listId:   9, campaignId: 've', key: 'inspiration-daily' },   // Daily Inspiration
    { listId:   0, campaignId: 'jg', key: 'idol-chatter' },        // Idol Chatter
    { listId: 90007, campaignId: 'vD', key: 'bible-reading' },    // Bible Reading v2
    { listId:    29, campaignId: 'vd', key: 'entertainment' },     // Entertainment
    { listId:     6, campaignId: 'vr', key: 'best-of-beliefnet' }, // Angel-Wisdom (discontinued)
    { listId:    21, campaignId: 'vZ', key: 'best-of-beliefnet' }, // Saint of the Day (discontinued)
    { listId:    22, campaignId: 'vF', key: 'best-of-beliefnet' }, // From the Kitchen (discontinued)
];

const FALLBACK_NEWSLETTER_KEY = 'best-of-beliefnet';

/**
 * Resolves a Beliefnet list ID, GetResponse campaign ID, or existing newsletter key
 * to the canonical Beehiiv newsletter key. Unknown values fall back to best-of-beliefnet.
 */
export function resolveToNewsletterKey(id: string): string {
    const normalized = String(id).trim();
    if (!normalized) return FALLBACK_NEWSLETTER_KEY;

    const numericId = Number(normalized);
    if (!isNaN(numericId) && /^\d+$/.test(normalized)) {
        // Numeric input — match by listId; listId 0 means no legacy mapping
        if (numericId === 0) return FALLBACK_NEWSLETTER_KEY;
        const match = NEWSLETTER_KEY_MAP.find((m) => m.listId === numericId);
        return match ? match.key : FALLBACK_NEWSLETTER_KEY;
    }

    // Non-numeric — match by campaignId or newsletterKey (pass-through if already a key)
    const match = NEWSLETTER_KEY_MAP.find(
        (m) => m.campaignId === normalized || m.key === normalized
    );
    return match ? match.key : FALLBACK_NEWSLETTER_KEY;
}
